import { Card, CardContent, CardHeader, CardTitle } from "@components/common/card"
import { Button } from "@components/common/button"
import useTranslator from "@hooks/use-translator"
import {
  createContext,
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react"
import WorkDiariesTable from "./work-diaries-table"
import { DictType } from "@pma/types"
import { useQuery } from "@tanstack/react-query"
import axios from "axios"
import {
  GET_CONTRACTOR,
  GET_DICTIONARY_RESOURCES_ENDPOINT,
  GET_STATUS,
  GET_WEATHER,
  GET_WORK_DIARY_BY_PROJECT,
  SERVER_URL,
  WORK_DIARY_GET_BY_ID,
} from "@pma/utils/api-routes"
import { parseCookies } from "nookies"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { WorkLogsFilter } from "../common/work-diaries/FilterSelect"
import { NewWorkDiaries } from "./grouped-tasks/New-work-diaries"
import { useAuth } from "@pma/stores/auth-store"
import { DateRange } from "react-day-picker"
import { Plus } from "lucide-react"
import { format } from "date-fns"
import { he } from "date-fns/locale"

interface Props {
  project: DictType
}

interface WorkRequestPayload {
  project_id: number
  date?: string
  supervisor_filter?: number
  search_text?: string
  building_filter?: string
  number_filter?: string
  paging_start_page: number
  paging_per_page: number
  paging_offset?: number
  show_deleted?: boolean
  period?: {
    from: string
    to: string
  }
  sorting?: {
    orderBy: "asc" | "desc" | string
    sortBy: string
  }
}

interface WorkDiaryContextProps {
  setSelectedWorkDiaryDetails: Dispatch<SetStateAction<any>>
}

export const WorkDiaryContext = createContext<WorkDiaryContextProps>({
  setSelectedWorkDiaryDetails: (state) => state,
})

const WorkDiariesList = ({ project }: Props) => {
  const { translate, locale } = useTranslator()
  const { authConfig } = useAuth()
  const isHebrew = locale === "he"
  const [taskModal, setTaskModal] = useState<boolean>(false)
  const [searchValue, setSearchValue] = useState("")
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("")
  const [diaryId, setDiaryId] = useState<number>(0)
  const [selectWorkDiaryDetails, setSelectedWorkDiaryDetails] = useState<any | undefined>(undefined)
  const [workDetails, setWorkDetails] = useState(undefined)
  const [showDeleted, setShowDeleted] = useState(false)

  const [filters, setFilters] = useState({
    building: "",
    supervisor: 0,
    type: "",
  })
  const [filterDateRange, setFilterDateRange] = useState<DateRange | undefined>(undefined)
  const [pagination, setPagination] = useState({
    pageIndex: 1,
    pageSize: 25,
  })
  const [pageSorting, setPageSorting] = useState({
    orderBy: "",
    sortBy: "",
  })

  // Memoize filter values to prevent unnecessary re-renders
  const memoizedFilters = useMemo(
    () => filters,
    [filters.building, filters.supervisor, filters.type],
  )
  const memoizedPagination = useMemo(() => pagination, [pagination.pageIndex, pagination.pageSize])
  const memoizedSorting = useMemo(() => pageSorting, [pageSorting.orderBy, pageSorting.sortBy])
  const memoizedDateRange = useMemo(
    () => filterDateRange,
    [filterDateRange?.from, filterDateRange?.to],
  )

  const accessToken = parseCookies()[ACCESS_TOKEN]
  const orgId = authConfig?.last_organization_id
  const projectId = project?.id

  const getWorkDiaries = async () => {
    const payload: WorkRequestPayload = {
      project_id: projectId,
      paging_start_page: memoizedPagination.pageIndex,
      paging_per_page: memoizedPagination.pageSize,
    }

    if (debouncedSearchValue) {
      payload["search_text"] = debouncedSearchValue
    }

    if (memoizedFilters.building) {
      payload["number_filter"] = memoizedFilters.building
    }

    if (memoizedFilters.supervisor) {
      payload["supervisor_filter"] = memoizedFilters.supervisor
    }

    if (memoizedFilters.type) {
      payload["building_filter"] = memoizedFilters.type
    }

    if (memoizedSorting.sortBy) {
      payload["sorting"] = memoizedSorting
    }

    if (showDeleted) {
      payload["show_deleted"] = showDeleted
    }

    if (memoizedDateRange?.from && memoizedDateRange?.to) {
      const startDate = format(memoizedDateRange?.from, "yyyy-MM-dd", {
        locale: isHebrew ? he : undefined,
      })
      const endDate = format(memoizedDateRange.to, "yyyy-MM-dd", {
        locale: isHebrew ? he : undefined,
      })

      payload["period"] = { from: startDate, to: endDate }
    }

    const response = await axios.post(SERVER_URL + GET_WORK_DIARY_BY_PROJECT, payload, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })

    return response.data
  }

  const {
    data: resWorkDiaries,
    isLoading,
    isFetching,
    refetch: refetchWorkDiary,
  } = useQuery({
    queryKey: [
      `work-diaries-context`,
      projectId,
      memoizedPagination.pageIndex,
      memoizedPagination.pageSize,
      debouncedSearchValue,
      memoizedFilters,
      memoizedDateRange,
      memoizedSorting,
      showDeleted,
    ],
    queryFn: getWorkDiaries,
    enabled: !!projectId, // Only run when project ID exists
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes (replaces cacheTime in newer versions)
    refetchOnWindowFocus: false, // Prevent refetch on window focus
    refetchOnMount: false, // Prevent refetch on component mount
    placeholderData: (previousData) => previousData, // Keep previous data while fetching new data
  })

  const paginationData = useMemo(() => {
    if (!resWorkDiaries) {
      return {
        curPage: 0,
        itemsReceived: 0,
        itemsTotal: 0,
        nextPage: 0,
        offset: 0,
        pageTotal: 0,
        prevPage: 0,
        pageSize: memoizedPagination.pageSize,
      }
    }

    return {
      curPage: resWorkDiaries?.curPage || 0,
      itemsReceived: resWorkDiaries?.itemsReceived || 0,
      itemsTotal: resWorkDiaries?.itemsTotal || 0,
      nextPage: resWorkDiaries?.nextPage || 0,
      offset: resWorkDiaries?.offset || 0,
      pageTotal: resWorkDiaries?.pageTotal || 0,
      prevPage: resWorkDiaries?.prevPage || 0,
      pageSize: memoizedPagination.pageSize,
    }
  }, [resWorkDiaries, memoizedPagination.pageSize])

  const getStatusByOrg = async () => {
    const response = await axios.get(SERVER_URL + GET_STATUS, {
      params: {
        organization_id: orgId,
      },
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })
    return response.data
  }
  const statusResult = useQuery({
    queryKey: ["status-context"],
    queryFn: () => getStatusByOrg(),
    staleTime: 1000 * 60 * 15, // 15 minutes for status data
    refetchOnWindowFocus: false,
  })

  const getContractorByProject = async () => {
    const response = await axios.get(SERVER_URL + GET_CONTRACTOR, {
      params: {
        project_id: projectId,
      },
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })
    return response.data
  }
  const contractorResult = useQuery({
    queryKey: ["contractor-context"],
    queryFn: () => getContractorByProject(),
    staleTime: 1000 * 60 * 15, // 15 minutes
    refetchOnWindowFocus: false,
  })

  // Debounced search to prevent excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchValue(searchValue)
    }, 300)

    return () => clearTimeout(timer)
  }, [searchValue])

  // Prevent unnecessary re-renders by memoizing the context value
  const contextValue = useMemo(
    () => ({
      setSelectedWorkDiaryDetails,
    }),
    [],
  )

  const handleFilterChange = useCallback(
    (filterName: keyof typeof filters, value: string | number) => {
      setFilters((prev) => ({ ...prev, [filterName]: value }))
    },
    [],
  )

  const getWeatherByOrg = async () => {
    const response = await axios.get(SERVER_URL + GET_WEATHER, {
      params: {
        organization_id: orgId,
      },
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })
    return response.data
  }
  const weatherResult = useQuery({
    queryKey: ["weather-context"],
    queryFn: () => getWeatherByOrg(),
    staleTime: 1000 * 60 * 30, // 30 minutes for weather data
    refetchOnWindowFocus: false,
  })

  const resourcesStructuresResult = useQuery({
    queryKey: ["resources-structures-context"],
    queryFn: () => getResourcesStructuresOrg("get_structures", true),
    enabled: true,
    // staleTime: 1000 * 60 * 20, // 20 minutes
    // refetchOnWindowFocus: false,
  })

  const resourcesWorkforceResult = useQuery({
    queryKey: ["resources-workforce-context"],
    queryFn: () => getResourcesStructuresOrg("get_employees", true),
    enabled: true,
    // staleTime: 1000 * 60 * 20, // 20 minutes
    // refetchOnWindowFocus: false,
  })

  const resourcesToolsResult = useQuery({
    queryKey: ["resources-tools-context"],
    queryFn: () => getResourcesStructuresOrg("get_tools", true),
    enabled: true,
    // staleTime: 1000 * 60 * 20, // 20 minutes
    // refetchOnWindowFocus: false,
  })

  const getResourcesStructuresOrg = async (key: string, value: boolean) => {
    const response = await axios.get(SERVER_URL + GET_DICTIONARY_RESOURCES_ENDPOINT, {
      params: {
        organization_id: orgId,
        [key]: value,
      },
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })
    return response.data
  }

  const getWorkDiaryDetailsById = async (diaryId: number) => {
    const response = await axios.get(SERVER_URL + WORK_DIARY_GET_BY_ID + diaryId, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })

    return response.data
  }

  const { data: workDiaryDetail, isFetching: workDiaryDetailIsFetching } = useQuery({
    queryKey: ["get-work-diary-details", diaryId],
    queryFn: () => getWorkDiaryDetailsById(diaryId),
    enabled: !!diaryId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
  })

  useEffect(() => {
    if (selectWorkDiaryDetails != undefined) {
      setDiaryId(selectWorkDiaryDetails?.id)
      setWorkDetails(selectWorkDiaryDetails)
      setTaskModal(true)
    }
  }, [selectWorkDiaryDetails])

  useEffect(() => {
    if (taskModal == false) {
      setDiaryId(0)
      setWorkDetails(undefined)
      setSelectedWorkDiaryDetails(undefined)
    }
  }, [taskModal])

  useEffect(() => {
    if (!workDiaryDetailIsFetching && workDiaryDetail) {
      setWorkDetails(workDiaryDetail)
    }
  }, [workDiaryDetailIsFetching, workDiaryDetail])

  // Check if essential data is ready to prevent flickering
  const isEssentialDataReady =
    statusResult.data &&
    contractorResult.data &&
    weatherResult.data &&
    resourcesStructuresResult.data &&
    resourcesWorkforceResult.data &&
    resourcesToolsResult.data

  // Check if essential data is loading to prevent flickering
  const isInitialLoading =
    isLoading ||
    statusResult.isLoading ||
    contractorResult.isLoading ||
    weatherResult.isLoading ||
    resourcesStructuresResult.isLoading ||
    resourcesWorkforceResult.isLoading ||
    resourcesToolsResult.isLoading

  // Early return while essential data is loading or not ready
  if (!projectId || isInitialLoading || !isEssentialDataReady) {
    return (
      <Card className='my-6 space-y-3 p-0'>
        <CardHeader>
          <CardTitle
            style={{ textAlign: isHebrew ? "right" : "left" }}
            className='flex w-full flex-col items-center justify-between text-xl font-semibold md:flex-row'
          >
            <span>{translate("Work Logs Management")}</span>
          </CardTitle>
        </CardHeader>
        <CardContent className='p-2 md:p-4'>
          <div className='flex items-center justify-center p-8'>
            <div className='h-8 w-8 animate-spin rounded-full border-b-2 border-primary'></div>
            <span className='ml-2'>{translate("Loading")}...</span>
          </div>
          {/* Skeleton loader for table */}
          <div className='mt-4 space-y-3'>
            <div className='h-4 animate-pulse rounded bg-gray-200'></div>
            <div className='h-4 w-3/4 animate-pulse rounded bg-gray-200'></div>
            <div className='h-4 w-1/2 animate-pulse rounded bg-gray-200'></div>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className='my-6 space-y-3 p-0'>
      <CardHeader>
        <CardTitle
          style={{ textAlign: isHebrew ? "right" : "left" }}
          className='flex w-full flex-col items-center justify-between text-xl font-semibold md:flex-row'
        >
          <span>{translate("Work Logs Management")}</span>
          <div className='flex flex-col items-center md:flex-row'>
            <Button
              size={"sm"}
              onClick={() => setTaskModal(!taskModal)}
              className='flex w-full items-center gap-2 md:w-32'
            >
              <Plus size={16} />
              {translate("Add Work Diaries")}{" "}
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <div>
        {!isFetching && (
          <WorkLogsFilter
            filters={filters}
            onFilterChange={handleFilterChange}
            project={project}
            filterDateRange={filterDateRange}
            setFilterDateRange={setFilterDateRange}
            search={searchValue}
            setSearch={setSearchValue}
            showDeleted={showDeleted}
            setShowDeleted={setShowDeleted}
          />
        )}
      </div>
      <CardContent className='p-2 md:p-4'>
        <WorkDiaryContext.Provider value={contextValue}>
          <WorkDiariesTable
            workDiaries={resWorkDiaries?.items || []}
            isLoading={isLoading}
            project={project}
            lstStatus={statusResult?.data}
            pagination={memoizedPagination}
            setPagination={setPagination}
            paginationData={paginationData}
            pageSorting={memoizedSorting}
            setPageSorting={setPageSorting}
          />
        </WorkDiaryContext.Provider>
      </CardContent>
      {taskModal && (
        <NewWorkDiaries
          taskModal={taskModal}
          setTaskModal={setTaskModal}
          lstStatus={statusResult?.data || []}
          lstWeather={weatherResult?.data || []}
          lstStructures={resourcesStructuresResult.data || []}
          lstWorkforce={resourcesWorkforceResult.data || []}
          lstTools={resourcesToolsResult.data || []}
          lstContractor={contractorResult?.data || []}
          project={project}
          workDiaries={workDetails}
          refetchWorkDiary={refetchWorkDiary}
          setDiaryId={setDiaryId}
        />
      )}
    </Card>
  )
}

export default WorkDiariesList
